/**
 * Hook for managing user unit preferences
 * Integrates with user profile and provides real-time unit conversion
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { UnitPreferences, DEFAULT_UNIT_PREFERENCES, UNIT_SYSTEMS } from '@/types/units';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/use-toast';
import { formatCricketSpeed, formatCricketDistance, formatCricketAngle } from '@/utils/unitConversion';

interface UnitPreferencesContextType {
  preferences: UnitPreferences;
  setPreferences: (preferences: Partial<UnitPreferences>) => Promise<void>;
  setUnitSystem: (system: keyof typeof UNIT_SYSTEMS) => Promise<void>;
  isLoading: boolean;
  reset: () => Promise<void>;
}

const UnitPreferencesContext = createContext<UnitPreferencesContextType | undefined>(undefined);

interface UnitPreferencesProviderProps {
  children: React.ReactNode;
}

export function UnitPreferencesProvider({ children }: UnitPreferencesProviderProps) {
  const { user, isAuthenticated } = useAuth();
  const [preferences, setPreferencesState] = useState<UnitPreferences>(DEFAULT_UNIT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);

  // Load preferences from localStorage or user profile
  const loadPreferences = useCallback(async () => {
    try {
      setIsLoading(true);
      
      if (isAuthenticated && user) {
        // Try to load from user profile/API
        // For now, we'll use localStorage as backend integration would be needed
        const stored = localStorage.getItem(`unit-preferences-${user.id}`);
        if (stored) {
          const parsed = JSON.parse(stored) as UnitPreferences;
          setPreferencesState({ ...DEFAULT_UNIT_PREFERENCES, ...parsed });
        } else {
          // Set intelligent defaults based on user's likely location
          // This could be enhanced with IP-based location detection
          const systemDefaults = UNIT_SYSTEMS.metric; // Default to metric
          setPreferencesState({
            ...DEFAULT_UNIT_PREFERENCES,
            ...systemDefaults
          });
        }
      } else {
        // Load from localStorage for guest users
        const stored = localStorage.getItem('unit-preferences-guest');
        if (stored) {
          const parsed = JSON.parse(stored) as UnitPreferences;
          setPreferencesState({ ...DEFAULT_UNIT_PREFERENCES, ...parsed });
        }
      }
    } catch (error) {
      console.error('Failed to load unit preferences:', error);
      setPreferencesState(DEFAULT_UNIT_PREFERENCES);
    } finally {
      setIsLoading(false);
    }
  }, [user, isAuthenticated]);

  // Save preferences
  const setPreferences = useCallback(async (newPreferences: Partial<UnitPreferences>) => {
    try {
      const updated = { ...preferences, ...newPreferences };
      setPreferencesState(updated);

      // Save to localStorage
      const key = isAuthenticated && user ? `unit-preferences-${user.id}` : 'unit-preferences-guest';
      localStorage.setItem(key, JSON.stringify(updated));

      // In a real app, also save to backend
      if (isAuthenticated && user) {
        // await userAPI.updatePreferences({ unitPreferences: updated });
        console.log('Unit preferences would be saved to backend for user:', user.id);
      }

      toast({
        title: 'Preferences Updated',
        description: 'Your unit preferences have been saved.',
      });
    } catch (error) {
      console.error('Failed to save unit preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to save unit preferences.',
        variant: 'destructive',
      });
    }
  }, [preferences, user, isAuthenticated]);

  // Set a complete unit system (metric, imperial, etc.)
  const setUnitSystem = useCallback(async (systemKey: keyof typeof UNIT_SYSTEMS) => {
    const system = UNIT_SYSTEMS[systemKey];
    await setPreferences(system);
  }, [setPreferences]);

  // Reset to defaults
  const reset = useCallback(async () => {
    await setPreferences(DEFAULT_UNIT_PREFERENCES);
  }, [setPreferences]);

  // Load preferences on mount and when user changes
  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const contextValue: UnitPreferencesContextType = {
    preferences,
    setPreferences,
    setUnitSystem,
    isLoading,
    reset,
  };

  return (
    <UnitPreferencesContext.Provider value={contextValue}>
      {children}
    </UnitPreferencesContext.Provider>
  );
}

export function useUnitPreferences(): UnitPreferencesContextType {
  const context = useContext(UnitPreferencesContext);
  if (context === undefined) {
    throw new Error('useUnitPreferences must be used within a UnitPreferencesProvider');
  }
  return context;
}

// Convenience hooks for common unit operations
export function useFormattedValue() {
  const { preferences } = useUnitPreferences();
  
  return useCallback((value: number, type: keyof typeof preferences.decimalPlaces) => {
    // This would integrate with the formatting utilities
    // For now, returning a simple implementation
    const unit = preferences[type];
    const decimals = preferences.decimalPlaces[type];
    return `${value.toFixed(decimals)} ${unit}`;
  }, [preferences]);
}

// Hook for cricket-specific formatting
export function useCricketFormat() {
  const { preferences } = useUnitPreferences();
  
  const formatSpeed = useCallback((valueInMps: number, includeUnit: boolean = true) => {
    return formatCricketSpeed(valueInMps, preferences, { includeUnit });
  }, [preferences]);

  const formatDistance = useCallback((valueInMeters: number, includeUnit: boolean = true) => {
    return formatCricketDistance(valueInMeters, preferences, { includeUnit });
  }, [preferences]);

  const formatAngle = useCallback((valueInDegrees: number, includeUnit: boolean = true) => {
    return formatCricketAngle(valueInDegrees, preferences, { includeUnit });
  }, [preferences]);

  return {
    formatSpeed,
    formatDistance,
    formatAngle,
    preferences,
  };
}